import React, { useMemo, useState } from 'react'
import { Table, Button, Badge, ButtonGroup, Alert, Input, FormGroup, Label } from 'reactstrap'
import { useQuery, useMutation } from '@apollo/client'
import { GET_ADMIN_CHANNELS, GET_ADMIN_INBOXES } from './Queries'
import { ARCHIVE_CHANNEL } from './Mutations'
import CreateEditChannelModal from './CreateEditChannelModal'

const CHANNEL_TYPE_LABELS = {
    'twilio_sms': 'Twilio SMS',
    'twilio_whatsapp': 'Twilio WhatsApp'
}

export default function ChannelManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedChannel, setSelectedChannel] = useState(null)
    const [showArchived, setShowArchived] = useState(false)
    const [filterInboxId, setFilterInboxId] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [feedback, setFeedback] = useState(null)

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { }
    })

    const { data, loading, error, refetch } = useQuery(GET_ADMIN_CHANNELS, {
        variables: { 
            inbox: filterInboxId || undefined
        },
        fetchPolicy: 'network-only'
    })

    const [archiveChannel] = useMutation(ARCHIVE_CHANNEL)

    const channels = data?.allChannels?.edges?.map(edge => edge.node) || []
    const inboxes = inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || []

    const filteredChannels = useMemo(() => {
        const byInbox = filterInboxId
            ? channels.filter(channel => channel.inbox?.id === filterInboxId)
            : channels

        if (!searchTerm.trim()) {
            return byInbox
        }

        const lower = searchTerm.trim().toLowerCase()
        return byInbox.filter(channel => {
            const channelName = channel.name?.toLowerCase() || ''
            const sendAs = channel.sendAs?.toLowerCase() || ''
            const inboxName = channel.inbox?.name?.toLowerCase() || ''
            return (
                channelName.includes(lower) ||
                sendAs.includes(lower) ||
                inboxName.includes(lower)
            )
        })
    }, [channels, filterInboxId, searchTerm])

    const closeModal = () => {
        setShowModal(false)
        setSelectedChannel(null)
    }

    const handleCreate = () => {
        setSelectedChannel(null)
        setShowModal(true)
    }

    const handleEdit = (channel) => {
        setSelectedChannel(channel)
        setShowModal(true)
    }

    const handleArchive = (channel, archive) => {
        if (window.confirm(`Are you sure you want to ${archive ? 'archive' : 'unarchive'} "${channel.name}"?`)) {
            archiveChannel({
                variables: {
                    id: channel.id,
                    isArchived: archive
                }
            })
                .then(({ data: mutationData }) => {
                    const response = mutationData?.messagingArchiveChannel
                    if (response?.ok) {
                        refetch()
                        setFeedback({
                            type: 'success',
                            message: `Channel "${channel.name}" ${archive ? 'archived' : 're-activated'} successfully.`
                        })
                    } else {
                        setFeedback({
                            type: 'danger',
                            message: response?.errors?.join(', ') || 'Unable to update channel status.'
                        })
                    }
                })
                .catch((mutationError) => {
                    setFeedback({
                        type: 'danger',
                        message: mutationError.message || 'Unexpected error updating channel.'
                    })
                })
        }
    }

    const handleModalSuccess = (message) => {
        refetch()
        if (message) {
            setFeedback({
                type: 'success',
                message
            })
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <Alert color="danger">Error loading channels: {error.message}</Alert>
    }

    const showEmptyState = filteredChannels.length === 0

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Channels</h3>
                    <p className="text-muted">Manage messaging channels (SMS, WhatsApp, etc.)</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleCreate}>
                        <i className="fa fa-plus"></i> Create Channel
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type} toggle={() => setFeedback(null)}>
                    {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0">
                    <Label for="inboxFilter" className="mr-2">Filter by Inbox:</Label>
                    <Input
                        type="select"
                        id="inboxFilter"
                        value={filterInboxId}
                        onChange={(e) => setFilterInboxId(e.target.value)}
                        style={{ width: '250px', display: 'inline-block' }}
                    >
                        <option value="">All Inboxes</option>
                        {inboxes.map((inbox) => (
                            <option key={inbox.id} value={inbox.id}>
                                {inbox.name}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search channels by name, inbox, or number…"
                    />
                </FormGroup>
            </div>

            {showEmptyState ? (
                <Alert color="info">
                    {searchTerm
                        ? 'No channels match your search.'
                        : showArchived 
                            ? 'No archived channels found.' 
                            : 'No channels found. Create your first channel to start messaging.'}
                </Alert>
            ) : (
                <Table responsive striped hover className="comms-admin-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Send As</th>
                            <th>Inbox</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredChannels.map((channel) => (
                            <tr key={channel.id}>
                                <td>
                                    <strong>{channel.name}</strong>
                                </td>
                                <td>
                                    <Badge color="info" className="badge-channel-type">
                                        {CHANNEL_TYPE_LABELS[channel.channelType] || channel.channelType}
                                    </Badge>
                                </td>
                                <td>
                                    <code>{channel.sendAs}</code>
                                </td>
                                <td>{channel.inbox?.name || 'N/A'}</td>
                                <td>
                                    {channel.isArchived ? (
                                        <Badge color="secondary">Archived</Badge>
                                    ) : (
                                        <Badge color="success">Active</Badge>
                                    )}
                                </td>
                                <td>
                                    <ButtonGroup size="sm">
                                        <Button
                                            color="info"
                                            outline
                                            onClick={() => handleEdit(channel)}
                                            title="Edit"
                                        >
                                            <i className="fa fa-edit"></i>
                                        </Button>
                                        <Button
                                            color={channel.isArchived ? "success" : "warning"}
                                            outline
                                            onClick={() => handleArchive(channel, !channel.isArchived)}
                                            title={channel.isArchived ? "Unarchive" : "Archive"}
                                        >
                                            <i className={`fa fa-${channel.isArchived ? 'undo' : 'archive'}`}></i>
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <CreateEditChannelModal
                isOpen={showModal}
                toggle={closeModal}
                channel={selectedChannel}
                onSuccess={handleModalSuccess}
            />
        </div>
    )
}
